import type { ConstValues } from '@openx/types/helpers.ts';
import { type AudiencePricing, MatchingOperator } from '@openx/types/targeting/audience.ts';

type SupportedMatchingOperator = ConstValues<typeof MatchingOperator>;

export type AudiencePricingWithSupportedOperator = Omit<AudiencePricing, 'operator'> & {
  operator: SupportedMatchingOperator;
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isSupportedMatchingOperator = (
  value: unknown
): value is (typeof MatchingOperator)[keyof typeof MatchingOperator] => {
  return value === MatchingOperator.INTERSECTION || value === MatchingOperator.UNION;
};

const isPricingByOrgEntry = (value: unknown): value is AudiencePricing['pricing_by_org'][string] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const entry = value as { price_pom?: unknown; price_cpm?: unknown };

  return isFiniteNumber(entry.price_pom) && isFiniteNumber(entry.price_cpm);
};

export const isAudiencePricing = (value: unknown): value is AudiencePricingWithSupportedOperator => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const pricing = value as {
    pricing_by_org?: unknown;
    totals?: unknown;
    operator?: unknown;
  };

  if (!pricing.pricing_by_org || typeof pricing.pricing_by_org !== 'object' || Array.isArray(pricing.pricing_by_org)) {
    return false;
  }

  if (!pricing.totals || typeof pricing.totals !== 'object' || Array.isArray(pricing.totals)) {
    return false;
  }

  const totals = pricing.totals as { price_pom?: unknown; price_cpm?: unknown };
  const operator = pricing.operator;

  if (!isSupportedMatchingOperator(operator)) {
    return false;
  }

  if (!isFiniteNumber(totals.price_pom) || !isFiniteNumber(totals.price_cpm)) {
    return false;
  }

  return Object.values(pricing.pricing_by_org).every(isPricingByOrgEntry);
};
